/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, TextField, IconButton,
    alpha, Divider, CircularProgress, Tooltip,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import useCustomSnackbar from 'src/hooks/useCustomSnackbar';
import { SYSTEM_FORM_TYPES } from 'src/pages/superAdmin/ConsentV2/consentConstants';
import { common } from 'src/theme/palette';

const TYPE_COLORS = {
    CONSENT: '#078DEE',
    MEDICAL_HISTORY: '#FF5630',
    SURVEY: '#855CF8',
    PATIENT_INTAKE: '#00AB55',
    REFERRAL: '#FFAB00',
    TREATMENT_PLAN: '#FF3030',
    OTHER: '#637381',
};

const getTypeColor = (formType) => TYPE_COLORS[formType] || '#637381';
const getTypeLabel = (formType) => {
    const found = SYSTEM_FORM_TYPES.find((t) => t.value === formType);
    return found?.label || formType?.replace(/_/g, ' ') || 'Other';
};

/**
 * SequencePresetManager — Full CRUD for form sequence presets.
 *
 * Two views:
 *   1. List view — shows all presets with apply, edit, delete actions
 *   2. Create/Edit view — name input + form selection from current selection or full list
 */
const SequencePresetManager = ({
    open,
    onClose,
    practiceId,
    sequencePresets,
    forms,
    selectedForms,
    onApplyPreset,
    onPresetsChanged,
    initialView,
}) => {
    const { enqueueSnackbar } = useCustomSnackbar();
    const [view, setView] = useState(initialView || 'list'); // 'list' | 'create' | 'edit'
    const [editingPreset, setEditingPreset] = useState(null);
    const [presetName, setPresetName] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);

    // Sync view with initialView when dialog opens
    useEffect(() => {
        if (open && initialView) {
            setView(initialView);
        }
    }, [open, initialView]);

    const formsList = useMemo(() => {
        const raw = Array.isArray(forms) ? forms : (forms?.results || []);
        return raw;
    }, [forms]);

    const formsMap = useMemo(() => {
        const map = {};
        formsList.forEach((f) => { map[f.id] = f; });
        return map;
    }, [formsList]);

    const handleStartCreate = () => {
        setView('create');
        setEditingPreset(null);
        setPresetName(selectedForms.length >= 2 ? '' : '');
    };

    const handleStartEdit = (preset) => {
        setView('edit');
        setEditingPreset(preset);
        setPresetName(preset.name || '');
    };

    const handleSave = async () => {
        if (!presetName.trim()) {
            enqueueSnackbar('Please enter a name for the preset', { variant: 'warning' });
            return;
        }

        const formIds = view === 'create'
            ? selectedForms.map((f) => f.id)
            : (editingPreset?.form_ids || []);

        if (formIds.length === 0) {
            enqueueSnackbar('Preset must include at least one form', { variant: 'warning' });
            return;
        }

        setSaving(true);
        try {
            if (view === 'create') {
                const res = await callApi(
                    `/api/v1/practices/${practiceId}/form-sequences/`,
                    'post',
                    { name: presetName.trim(), form_ids: formIds }
                );
                if (res.status === 201 || res.status === 200) {
                    enqueueSnackbar('Preset saved', { variant: 'success' });
                    onPresetsChanged();
                    setView('list');
                    setPresetName('');
                } else if (res.status === 404) {
                    enqueueSnackbar('Presets feature is not yet available on this server', { variant: 'warning' });
                } else {
                    const detail = typeof res.data?.detail === 'string' ? res.data.detail : 'Failed to save preset';
                    enqueueSnackbar(detail, { variant: 'error' });
                }
            } else {
                const res = await callApi(
                    `/api/v1/practices/${practiceId}/form-sequences/${editingPreset.id}/`,
                    'patch',
                    { name: presetName.trim() }
                );
                if (res.status === 200) {
                    enqueueSnackbar('Preset updated', { variant: 'success' });
                    onPresetsChanged();
                    setView('list');
                    setPresetName('');
                    setEditingPreset(null);
                } else if (res.status === 404) {
                    enqueueSnackbar('Presets feature is not yet available on this server', { variant: 'warning' });
                } else {
                    const detail = typeof res.data?.detail === 'string' ? res.data.detail : 'Failed to update preset';
                    enqueueSnackbar(detail, { variant: 'error' });
                }
            }
        } catch {
            enqueueSnackbar('Something went wrong', { variant: 'error' });
        }
        setSaving(false);
    };

    const handleDelete = async (presetId) => {
        setDeleting(presetId);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/form-sequences/${presetId}/`,
                'delete'
            );
            if (res.status === 204 || res.status === 200) {
                enqueueSnackbar('Preset deleted', { variant: 'success' });
                onPresetsChanged();
            } else {
                enqueueSnackbar('Failed to delete preset', { variant: 'error' });
            }
        } catch {
            enqueueSnackbar('Failed to delete preset', { variant: 'error' });
        }
        setDeleting(null);
    };

    const handleClose = () => {
        setView('list');
        setPresetName('');
        setEditingPreset(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            // NOTE: do NOT set `overflow: 'hidden'` on the Paper — combined
            // with size="small" + autoFocus on the Preset name TextField,
            // the OutlinedInput's notched outline gets clipped and the
            // floating label is bisected by the top border. The Dialog's
            // own scroll container handles overflow correctly.
            PaperProps={{ sx: { borderRadius: '14px' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{
                    width: 32, height: 32, borderRadius: '8px',
                    bgcolor: alpha('#078DEE', 0.1), color: '#078DEE',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Iconify icon="solar:playlist-bold" width={18} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '15px' }}>
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {view === 'list' ? 'Form Presets' : (view === 'create' ? 'Save New Preset' : 'Edit Preset')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {view === 'list'
                            ? 'Reusable form sequences for quick selection'
                            : (view === 'create' ? 'Save your current form selection as a preset' : 'Rename this preset')}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={handleClose} sx={{ color: 'text.disabled' }}>
                    <Iconify icon="eva:close-fill" width={18} />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 2.5 }}>
                {/* ── List view ────────────────────────────────────────────── */}
                {view === 'list' && (
                    <Box>
                        {sequencePresets.length === 0 ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <Iconify icon="solar:playlist-minimalistic-bold" width={40} sx={{ color: '#C4CDD5', mb: 1.5 }} />
                                <Typography variant="body2" sx={{ color: '#919EAB', mb: 0.5, fontWeight: 600 }}>
                                    No presets yet
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#C4CDD5' }}>
                                    Select forms and save as a preset for quick reuse
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {sequencePresets.map((preset) => (
                                    <Box
                                        key={preset.id}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: '10px',
                                            border: '1px solid',
                                            borderColor: alpha('#919EAB', 0.2),
                                            '&:hover': { borderColor: alpha('#078DEE', 0.3), bgcolor: alpha('#078DEE', 0.02) },
                                            transition: 'all 0.15s ease',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                            <Iconify icon="solar:playlist-minimalistic-bold" width={16} sx={{ color: '#078DEE' }} />
                                            <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, fontSize: '13px' }}>
                                                {preset.name}
                                            </Typography>
                                            <Tooltip title="Apply this preset">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => { onApplyPreset(preset.id); handleClose(); }}
                                                    sx={{ color: common.mintyZest, '&:hover': { bgcolor: alpha(common.mintyZest, 0.08) } }}
                                                >
                                                    <Iconify icon="eva:arrow-circle-right-fill" width={18} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit name">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleStartEdit(preset)}
                                                    sx={{ color: '#637381', '&:hover': { bgcolor: alpha('#637381', 0.08) } }}
                                                >
                                                    <Iconify icon="eva:edit-2-fill" width={16} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete preset">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDelete(preset.id)}
                                                    disabled={deleting === preset.id}
                                                    sx={{ color: '#FF4842', '&:hover': { bgcolor: alpha('#FF4842', 0.08) } }}
                                                >
                                                    {deleting === preset.id
                                                        ? <CircularProgress size={14} sx={{ color: '#FF4842' }} />
                                                        : <Iconify icon="eva:trash-2-fill" width={16} />}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        {/* Form chips in preset */}
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 3.5 }}>
                                            {(preset.form_ids || []).map((fid, idx) => {
                                                const form = formsMap[fid];
                                                if (!form) {
                                                    return (
                                                        <Box key={fid} sx={{
                                                            px: '8px', py: '2px', borderRadius: '12px', fontSize: '10px',
                                                            bgcolor: alpha('#919EAB', 0.08), color: '#919EAB', fontWeight: 500,
                                                        }}>
                                                            #{idx + 1} Unknown
                                                        </Box>
                                                    );
                                                }
                                                const color = getTypeColor(form.form_type);
                                                return (
                                                    <Box key={fid} sx={{
                                                        px: '8px', py: '2px', borderRadius: '12px', fontSize: '10px',
                                                        bgcolor: alpha(color, 0.08), color,
                                                        fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5,
                                                    }}>
                                                        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: color }} />
                                                        {form.name}
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}

                {/* ── Create / Edit view ──────────────────────────────────── */}
                {(view === 'create' || view === 'edit') && (
                    <Box>
                        <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            label="Preset name"
                            placeholder="e.g. New Patient Onboarding"
                            value={presetName}
                            onChange={(e) => setPresetName(e.target.value)}
                            // `shrink: true` keeps the label permanently in
                            // the shrunken position so the OutlinedInput
                            // notch is rendered correctly regardless of
                            // focus state. Without this, the autoFocus
                            // race could leave the notch un-cut and the
                            // top border would slice through the label.
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                mt: 1,
                                mb: 2,
                                '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                            }}
                        />

                        {/* Preview of forms to be included */}
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
                            {view === 'create' ? 'Forms to include' : 'Forms in this preset'}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {(view === 'create' ? selectedForms : (editingPreset?.form_ids || []).map((fid) => formsMap[fid]).filter(Boolean)).map((form, idx) => {
                                const color = getTypeColor(form.form_type);
                                return (
                                    <Box
                                        key={form.id}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
                                            borderRadius: '8px', border: '1px solid', borderColor: alpha(color, 0.2),
                                            bgcolor: alpha(color, 0.03),
                                        }}
                                    >
                                        <Box sx={{
                                            width: 20, height: 20, borderRadius: '6px',
                                            bgcolor: alpha(color, 0.12), color,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '10px', fontWeight: 700, flexShrink: 0,
                                        }}>
                                            {idx + 1}
                                        </Box>
                                        <Typography variant="body2" sx={{ flex: 1, fontSize: '12px', fontWeight: 600, color: '#212B36' }}>
                                            {form.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: '10px', fontWeight: 600, color, textTransform: 'uppercase' }}>
                                            {getTypeLabel(form.form_type)}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>

                        {view === 'create' && selectedForms.length < 2 && (
                            <Typography variant="caption" sx={{ color: '#FF4842', mt: 1, display: 'block' }}>
                                Select at least 2 forms in the main dialog to save as a preset
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
                {view === 'list' ? (
                    <>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClose}
                            sx={{ borderRadius: '8px', borderColor: 'divider', color: 'text.secondary' }}
                        >
                            Close
                        </Button>
                        {selectedForms.length >= 2 && (
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleStartCreate}
                                startIcon={<Iconify icon="eva:plus-fill" width={16} />}
                                sx={{
                                    borderRadius: '8px',
                                    bgcolor: common.mintyZest,
                                    '&:hover': { bgcolor: '#007B40' },
                                    fontWeight: 700,
                                }}
                            >
                                Save current as preset
                            </Button>
                        )}
                    </>
                ) : (
                    <>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => { setView('list'); setPresetName(''); setEditingPreset(null); }}
                            sx={{ borderRadius: '8px', borderColor: 'divider', color: 'text.secondary' }}
                        >
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleSave}
                            disabled={saving || !presetName.trim() || (view === 'create' && selectedForms.length < 2)}
                            startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="eva:save-fill" width={16} />}
                            sx={{
                                borderRadius: '8px',
                                bgcolor: common.mintyZest,
                                '&:hover': { bgcolor: '#007B40' },
                                fontWeight: 700,
                            }}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

SequencePresetManager.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    sequencePresets: PropTypes.array.isRequired,
    forms: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    selectedForms: PropTypes.array,
    onApplyPreset: PropTypes.func.isRequired,
    onPresetsChanged: PropTypes.func.isRequired,
    initialView: PropTypes.string,
};

SequencePresetManager.defaultProps = {
    forms: [],
    selectedForms: [],
    initialView: 'list',
};

export default SequencePresetManager;
